import { useCallback, type ReactNode } from 'react'

import RichText from '@/components/ui/rich-text'
import Link from '@/components/link-component'
import ImageContentful from '@/components/ui/image-contentful'
import Image from '@/components/ui/image'

import { cx } from '@/utils/strings'
import { type TypeComponentQuoteSkeleton } from '@/types/ctf/TypeComponentQuote'
import type { Entry } from 'contentful'
import colors from '@/constants/colors'
import CtaLink from './cta-link'

type QuoteEntry = Entry<TypeComponentQuoteSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

const { gold, teal, violet, melon } = colors

export default function Quote({
  actions,
  attribution,
  attributorTitle,
  attributorImage,
  attributionPosition,
  body,
  borderColor = 'Violet',
  image,
  quoteImage,
  link,
  className,
}: QuoteEntry['fields']) {
  const LinkWrap = useCallback(
    ({ children, to }: { children: ReactNode; to?: string }) => (
      <>
        {to ? (
          <Link className="w-full" to={to}>
            {children}
          </Link>
        ) : (
          children
        )}
      </>
    ),
    []
  )

  const renderAttributionWithImage = () => (
    <div className="flex gap-4 items-center mb-5">
      {attributorImage?.fields && (
        <div className="rounded-full border-2 border-secondary-100  w-16 h-16 flex justify-center items-center object-cover [&_figure]:mb-0!">
          <ImageContentful
            loading="eager"
            {...attributorImage?.fields.image.fields}
            darkModeImage={attributorImage?.fields?.imageDarkMode?.fields}
            className="rounded-full object-cover w-16 h-16"
          />
        </div>
      )}
      <div className={cx('text-xl [&_cite]:not-italic [&_cite]:block ', !attributionPosition && 'mt-4 mb-1')}>
        <cite className="text-lg font-semibold">{attribution}</cite>
        <p className="mb-0">{attributorTitle}</p>
      </div>
    </div>
  )

  return (
    <LinkWrap to={link?.fields && link.fields.url}>
      <figure
        className={cx(
          'p-6 mb-1 rounded-lg bg-white dark:bg-secondary-800 border @container',

          className
        )}
      >
        {attribution && attributionPosition && renderAttributionWithImage()}
        <blockquote className="[&_p]:text-xl xl:[&_p]:text-2xl leading-normal font-normal inline-block">
          <RichText body={body} />
        </blockquote>

        {((image?.fields && quoteImage?.fields) || quoteImage?.fields) && (
          <div className={cx('mt-6 w-[150px]')}>
            {quoteImage?.fields && (
              <ImageContentful
                loading="eager"
                {...quoteImage?.fields.image.fields}
                darkModeImage={quoteImage?.fields?.imageDarkMode?.fields}
                className="[&_svg]:fill-inherit"
              />
            )}
          </div>
        )}

        {image?.fields && !quoteImage?.fields && (
          <div className={cx('mt-6 w-[150px]')}>
            {image?.fields && <ImageContentful loading="eager" {...image?.fields} className="[&_svg]:fill-inherit" />}
          </div>
        )}

        {attribution && !attributionPosition && renderAttributionWithImage()}

        {actions && (
          <div className="flex flex-row gap-6 px-2">
            {actions.map((action) => (
              <CtaLink key={action?.sys.id} {...action} to={action.fields.link.fields.url}>
                {action.fields.label}
              </CtaLink>
            ))}
          </div>
        )}
      </figure>
    </LinkWrap>
  )
}
